

import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * ✅ 드로어 열릴 때마다 내부(아코디언 포함) details를 전부 초기화
   * - 루트(details#Details-menu-drawer-container)는 제외
   */
  resetInnerDetails() {
    const root = this.refs.details;

    const innerDetails = this.querySelectorAll('details');
    innerDetails.forEach((el) => {
      if (!(el instanceof HTMLDetailsElement)) return;
      if (el === root) return; // 루트 드로어는 유지(열릴 예정이니까)
      reset(el); // open 제거 + aria/클래스 초기화
    });
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // ✅ "메뉴 열 때는 항상 닫힌 상태" 요구사항 반영:
    // 메인 드로어를 여는 순간, 내부 아코디언/서브메뉴 details 전부 reset
    if (details === this.refs.details) {
      this.resetInnerDetails();
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}


// 전체 코드

// import { Component } from '@theme/component';
// import { trapFocus, removeTrapFocus } from '@theme/focus';
// import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

// /**
//  * A custom element that manages the main menu drawer.
//  *
//  * @typedef {object} Refs
//  * @property {HTMLDetailsElement} details - The details element.
//  *
//  * @extends {Component<Refs>}
//  */
// class HeaderDrawer extends Component {
//   requiredRefs = ['details'];

//   connectedCallback() {
//     super.connectedCallback();

//     this.addEventListener('keyup', this.#onKeyUp);
//     this.#setupAnimatedElementListeners();
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.removeEventListener('keyup', this.#onKeyUp);
//   }

//   /**
//    * Close the main menu drawer when the Escape key is pressed
//    * @param {KeyboardEvent} event
//    */
//   #onKeyUp = (event) => {
//     if (event.key !== 'Escape') return;

//     this.#close(this.#getDetailsElement(event));
//   };

//   /**
//    * @returns {boolean} Whether the main menu drawer is open
//    */
//   get isOpen() {
//     return this.refs.details.hasAttribute('open');
//   }

//   /**
//    * Get the closest details element to the event target
//    * @param {Event | undefined} event
//    * @returns {HTMLDetailsElement}
//    */
//   #getDetailsElement(event) {
//     if (!(event?.target instanceof Element)) return this.refs.details;

//     return event.target.closest('details') ?? this.refs.details;
//   }

//   /**
//    * Toggle the main menu drawer
//    */
//   toggle() {
//     return this.isOpen ? this.close() : this.open();
//   }

//   /**
//    * Open the closest drawer or the main menu drawer
//    * @param {Event} [event]
//    */
//   open(event) {
//     const details = this.#getDetailsElement(event);
//     const summary = details.querySelector('summary');

//     if (!summary) return;

//     summary.setAttribute('aria-expanded', 'true');

//     this.preventInitialAccordionAnimations(details);
//     requestAnimationFrame(() => {
//       details.classList.add('menu-open');

//       // Wait for the drawer animation to complete before trapping focus
//       const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
//       onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
//     });
//   }

//   /**
//    * Go back or close the main menu drawer
//    * @param {Event} [event]
//    */
//   back(event) {
//     this.#close(this.#getDetailsElement(event));
//   }

//   /**
//    * Close the main menu drawer
//    */
//   close() {
//     this.#close(this.refs.details);
//   }

//   /**
//    * Close the closest menu or submenu that is open
//    *
//    * @param {HTMLDetailsElement} details
//    */
//   #close(details) {
//     const summary = details.querySelector('summary');

//     if (!summary) return;

//     summary.setAttribute('aria-expanded', 'false');
//     details.classList.remove('menu-open');

//     // Wait for the .menu-drawer element's transition, not the entire details subtree
//     // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
//     const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

//     onAnimationEnd(
//       drawer || details,
//       () => {
//         reset(details);
//         if (details === this.refs.details) {
//           removeTrapFocus();
//           const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
//           openDetails.forEach(reset);
//         } else {
//           trapFocus(this.refs.details);
//         }
//       },
//       { subtree: false }
//     );
//   }

//   /**
//    * Attach animationend event listeners to all animated elements to remove will-change after animation
//    * to remove the stacking context and allow submenus to be positioned correctly
//    */
//   #setupAnimatedElementListeners() {
//     const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
//     allAnimated.forEach((element) => {
//       element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
//     });
//   }

//   /**
//    * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
//    * Adds a no-animation class to accordion content elements, then removes it after 100ms to
//    * re-enable animations for user interactions.
//    * @param {HTMLDetailsElement} details - The details element containing the accordions
//    */
//   preventInitialAccordionAnimations(details) {
//     const content = details.querySelectorAll('accordion-custom .details-content');

//     content.forEach((element) => {
//       if (element instanceof HTMLElement) {
//         element.classList.add('details-content--no-animation');
//       }
//     });
//     setTimeout(() => {
//       content.forEach((element) => {
//         if (element instanceof HTMLElement) {
//           element.classList.remove('details-content--no-animation');
//         }
//       });
//     }, 100);
//   }
// }

// if (!customElements.get('header-drawer')) {
//   customElements.define('header-drawer', HeaderDrawer);
// }

// /**
//  * Reset an open details element to its original state
//  *
//  * @param {HTMLDetailsElement} element
//  */
// function reset(element) {
//   element.classList.remove('menu-open');
//   element.removeAttribute('open');
//   element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
// }
